import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ColumnMode, DatatableComponent, NgxDatatableModule } from '@siemens/ngx-datatable';
import { ToastrService } from '@shared/services/toastr.service';
import { AuthService } from '@shared/services/auth.service';
import { HostModeService } from '@shared/services/hostMode.service';
import { distinctUntilChanged, Observable, startWith, Subject, switchMap } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import _ from 'lodash';
import { AdminOrganizationService } from '@shared/services/admin-organization.service';
import { AdminSiteService, Site } from '@shared/services/admin-site.service';
import { SpringPage } from '@shared/page';
import { AdminClusterService } from '@shared/services/admin-cluster.service';
import { SiteService, SiteStatus } from '@shared/services/site.service';
import { ConfirmService } from '@shared/services/confirm.service';
import { NgSelectComponent } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { SiteStatusComponent } from '@shared/components/site-status/site-status.component';
import { NgFor, NgIf } from '@angular/common';
import { RouterLink } from '@angular/router';
import { BadgeOrganizationComponent } from '@shared/components/badge-organization/badge-organization.component';
import { TimestampCellComponent } from '@shared/components/timestamp-cell/timestamp-cell.component';
import * as pager from '@shared/pager';

@Component({
  selector: 'app-sites',
  templateUrl: './sites.component.html',
  styleUrls: ['./sites.component.scss'],
  imports: [
    NgSelectComponent,
    FormsModule,
    NgbTooltip,
    NgxDatatableModule,
    SiteStatusComponent,
    NgIf,
    RouterLink,
    BadgeOrganizationComponent,
    TimestampCellComponent,
    NgFor,
    TranslatePipe,
  ],
})
export class SitesComponent implements OnInit, AfterViewInit {
  lang: string;

  sites: Site[];
  private filters$ = new Subject<SiteFilters>();
  filters: SiteFilters = {};
  clusters = [];
  siteStatuses: SiteStatus[] = [];
  curPage: number = 1;
  pageSize = 20;
  totalElements = null;
  organizations = [];

  @ViewChild('searchInput') searchInput: ElementRef;

  @ViewChild('myTable') set datatable(table: DatatableComponent) {
    table.columnMode = ColumnMode.force; // make resizing table header working
  }

  constructor(
    private adminClusterService: AdminClusterService,
    public translate: TranslateService,
    private toastr: ToastrService,
    public hostModeService: HostModeService,
    private adminSiteService: AdminSiteService,
    private siteService: SiteService,
    private adminOrganizationService: AdminOrganizationService,
    private auth: AuthService,
    private confirmService: ConfirmService,
  ) {}

  ngOnInit() {
    this.getClusters();
    this.getSiteStatuses();
    this.lang = this.auth.getCurrentLanguage();
    this.filters$
      .pipe(
        startWith({}),
        debounceTime(300),
        distinctUntilChanged(_.isEqual),
        switchMap((filters) => this.searchSites(filters)),
      )
      .subscribe({
        next: (page: any) => {
          this.sites = page.content;
          this.totalElements = page.totalElements;
        },
        error: () => {
          this.sites = [];
          this.totalElements = null;
        },
      });
    this.changeSearch();
  }

  ngAfterViewInit(): void {}

  confirmCron12H() {
    this.confirmService.confirm({ html: this.translate.instant('VoulezVousExecuterCron') }, () => this.cron12H());
  }

  private cron12H() {
    this.adminSiteService.refreshAllSites().subscribe({
      next: (_: any) => {
        this.toastr.success('CRONExecuteeSucces');
        this.getSites();
      },
      error: () => {
        this.toastr.error('CRONExecuteeEchoue');
      },
    });
  }

  confirmProvCluster() {
    this.confirmService.confirm({ html: this.translate.instant('ConfirmProvCluster') }, () => this.provCluster());
  }

  private provCluster() {
    this.adminClusterService.reprovAll(this.filters.cluster).subscribe((res: any) => {
      if (!res.hasError) {
        this.toastr.success('CRONExecuteeSucces');
        this.getSites();
      } else {
        this.toastr.error('CRONExecuteeEchoue');
      }
    });
  }

  changePage(evt) {
    this.curPage = evt.page;
    this.getSites();
  }

  changeSearch() {
    this.curPage = 1;
    this.filters$.next({ ...this.filters });
  }

  getSites() {
    this.searchSites(this.filters).subscribe((res: any) => {
      this.sites = res?.content || [];
      this.totalElements = res?.totalElements;
      this.curPage = res?.number + 1;
    });
  }

  searchSites(filters: SiteFilters): Observable<SpringPage<Site>> {
    return this.adminSiteService.getSites(
      { ...filters, contractIsNull: filters.contractIsNull ? true : null },
      { number: this.curPage - 1, size: this.pageSize },
    );
  }

  getClusters() {
    this.adminClusterService.getAll().subscribe((res) => {
      this.clusters = res;
    });
  }

  getSiteStatuses() {
    this.siteService.getStatuses().subscribe((statuses) => (this.siteStatuses = statuses));
  }

  refreshStatus(site: Site) {
    this.confirmService.confirm({ html: this.translate.instant('VoulezVousLancerCorrectionSite') }, () => {
      this.adminSiteService.refreshStatus(site.domainName).subscribe((res: any) => {
        if (!res.hasError) {
          this.toastr.success('CorrectionSucces');
        }
      });
    });
  }

  forceProvSite(site: Site) {
    this.confirmService.confirm({ html: this.translate.instant('VoulezVousReprovisionnerSite') }, () => {
      this.adminSiteService.provision(site.domainName).subscribe((res: any) => {
        if (!res.hasError) {
          this.toastr.success('SiteReprovisionneSucces');
        }
      });
    });
  }

  setMode(site: Site, event) {
    this.adminSiteService.updateSite(site.domainName, { mode: event.target.value }).subscribe({
      next: () => {
        this.toastr.success();
      },
      error: () => {
        event.target.value = site.mode;
      },
    });
  }

  async updateOrganizationOwner(siteName, organizationCode: string) {
    try {
      await this.adminOrganizationService.changeSiteOrganizationOwner(siteName, organizationCode);
      this.toastr.success();
      this.getSites();
    } catch (error) {
      return this.toastr.error(`${error}`, 'Erreur');
    }
  }

  async loadOrganizations(searchTerm) {
    if (searchTerm.length >= 2) {
      let res: any = null;
      try {
        res = await this.adminOrganizationService.getOrganizations({ searchTerm });
      } catch (error) {
        return this.toastr.error(error.error);
      }

      res.content = res.content.map((o) => {
        return { nameAndCode: o.name + ' - ' + o.code, ...o };
      });
      this.organizations = res.content;
    }
  }

  updateManaged(site: Site) {
    this.adminSiteService.setManaged(site.domainName, site.managed).subscribe({
      next: () => {
        this.toastr.success();
        this.getSites();
      },
      error: () => {
        this.getSites();
      },
    });
  }

  protected readonly pager = pager;
}

interface SiteFilters {
  cluster?: string;
  status?: string;
  mode?: string;
  filter?: string;
  contractIsNull?: boolean;
}
